import clsx from 'clsx'
import { computed, type DeepReadonly, defineComponent } from 'vue'
import { RouterLink } from 'vue-router'

import VButton from '@/components/common/button/VButton'
import PromptModal from '@/components/common/modal/PromptModal'
import VChip from '@/components/common/VChip'
import VIcon from '@/components/common/VIcon'
import VMenu, { type MenuItemProps, type VMenuActivatorProps } from '@/components/common/VMenu'
import useLocalization from '@/composables/localization/useLocalization'
import useBookingListItem from '@/composables/views/booking-list/useBookingListItem'
import { type Booking } from '@/types/Booking'
import { getStatusBgColor } from '@/utils/booking'

interface Props {
  item: Booking | DeepReadonly<Booking>
}

export default defineComponent<Props>((props) => {
  const { localeTexts } = useLocalization()
  const { cancelBookingModal, visitDate, bookingCancelModalParagraph, handleCancelBookingClick } = useBookingListItem(props)

  // #region Getters

  const statusClass = computed((): string => {
    return getStatusBgColor(props.item.booking_status)
  })

  const vip = computed((): string => {
    return props.item.is_patient_vip ? '(VIP)' : ''
  })

  // #endregion

  return () => (
    <RouterLink
      t-data='boards-list-item'
      class={[
        'block border-b border-b-neutral-300 p-4'
      ]}
      to={`booking-details/${props.item.id}`}
    >
      <div
        class='flex w-full justify-between'
        t-data='boards-list-list-item'
      >
        <div class='flex w-full'>
          <div class='flex-1 flex-col'>
            <div class='flex space-x-4'>
              <div
                t-data='boards-list-list-item-name'
                class='font-medium'
              >
                {props.item.patient_first_name} {props.item.patient_last_name}
              </div>
              <div t-data='boards-list-list-item-vip'>{vip.value}</div>
            </div>
            <div
              t-data='boards-list-list-item-id'
              class='mt-2 text-neutral-500'
            >
              {localeTexts.value['agent-main-booking-list-booking-id-th']}: {props.item.booking_no}
            </div>
            <div
              t-data='boards-list-list-item-visit-date'
              class='text-neutral-500'
            >
              {localeTexts.value['agent-main-booking-list-visit-date-th']}: {visitDate.value}
            </div>
          </div>
          <div
            t-data='boards-list-list-item-status'
            class='flex flex-col items-end justify-between'
          >
            <VChip class={statusClass.value}>
              {props.item.booking_status}
            </VChip>
            <div>
              <VMenu
                t-data='booking-list-list-item-menu'
                menuContentClass={clsx('w-60 origin-top-right py-2')}
                isMobileMode
              >
                {{
                  activator: (slotProps: VMenuActivatorProps) => (
                    <VButton
                      t-data='booking-list-list-item-menu-button'
                      mainRef={slotProps.ref}
                      class='btn-small text-caption text-neutral-800'
                      color={clsx('btn-white')}
                      onClick={slotProps.onClick}
                    >
                      <VIcon
                        class='text-[24px]'
                        icon='material-symbols:more-horiz'
                      />
                    </VButton>
                  ),
                  default: (slotProps: MenuItemProps) => (
                    <button
                      t-data='cancel-booking-btn'
                      class='menu-btn menu-btn-gray w-full'
                      onClick={(e) => {
                        handleCancelBookingClick()
                        slotProps.onClick?.(e)
                      }}
                    >
                      <span>{localeTexts.value['agent-main-booking-list-menu-item-cancel-btn']}</span>
                    </button>
                  )
                }}
              </VMenu>
            </div>
          </div>
        </div>
        {cancelBookingModal.isRevealed.value &&
          <PromptModal
            tData='cancel-booking-modal'
            modalkey={cancelBookingModal.key}
            title={localeTexts.value['booking-cancel-title']}
            content={bookingCancelModalParagraph.value}
            placeholder={localeTexts.value['booking-cancel-reason-placeholder']}
            cancelText={localeTexts.value['booking-cancel-cancel-btn']}
            confirmText={localeTexts.value['booking-cancel-confirm-btn']}
            confirmColor={clsx('btn-danger')}
          />}
      </div>
    </RouterLink>
  )
}, {
  name: 'BookingListListItem',
  props: ['item']
})
